/**
 * Internet Menu Service
 * 
 * This service attempts to fetch restaurant menu items from the internet
 * using Google Places API and ChatGPT API as fallback.
 * 
 * Note: Requires API keys to be set in environment variables:
 * - REACT_APP_GOOGLE_PLACES_API_KEY (optional, for Google Places API)
 * - REACT_APP_API_URL (required for ChatGPT API web search - points to Lambda/API)
 */

import type { RestaurantMenuItem } from '../data/restaurantMenus';
import { searchMenuApis } from './menuApiService';
import { getFallbackMenuItems as getRestaurantFallbacks } from '../data/restaurantFallbacks';

export interface InternetMenuResult {
  restaurantName: string;
  items: RestaurantMenuItem[];
  source: 'google_places' | 'web_search' | 'not_found';
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Search for restaurant menu using Google Places API
 */
async function searchGooglePlaces(restaurantName: string, location?: string): Promise<InternetMenuResult | null> {
  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
  
  if (!apiKey) {
    console.log('Google Places API key not configured');
    return null;
  }

  try {
    // Step 1: Search for the restaurant
    const searchUrl = `https://maps.googleapis.com/maps/api/place/textsearch/json?query=${encodeURIComponent(restaurantName + (location ? ` ${location}` : ''))}&type=restaurant&key=${apiKey}`;
    
    const searchResponse = await fetch(searchUrl);
    const searchData = await searchResponse.json();
    
    if (searchData.status !== 'OK' || !searchData.results || searchData.results.length === 0) {
      return null;
    }
    
    const placeId = searchData.results[0].place_id;
    
    // Step 2: Get place details including menu
    const detailsUrl = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&fields=name,menu&key=${apiKey}`;
    
    const detailsResponse = await fetch(detailsUrl);
    const detailsData = await detailsResponse.json();
    
    if (detailsData.status !== 'OK' || !detailsData.result) {
      return null;
    }
    
    // Step 3: Extract menu items
    const menuItems: RestaurantMenuItem[] = [];
    
    if (detailsData.result.menu) {
      // Google Places API menu structure
      const menu = detailsData.result.menu;
      
      // Parse menu sections and items
      if (menu.menuSections) {
        menu.menuSections.forEach((section: any) => {
          if (section.menuItems) {
            section.menuItems.forEach((item: any) => {
              if (item.name) {
                menuItems.push({
                  name: item.name,
                  explanation: item.description || `Menu item from ${restaurantName}`,
                  acneImpactScore: 50, // Will be analyzed later
                  restaurantName: restaurantName,
                });
              }
            });
          }
        });
      }
    }
    
    if (menuItems.length > 0) {
      return {
        restaurantName: detailsData.result.name || restaurantName,
        items: menuItems,
        source: 'google_places',
        confidence: 'high',
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching from Google Places API:', error);
    return null;
  }
}

/**
 * Search for restaurant menu using ChatGPT API
 */
function getProxyUrl(): string | null {
  const raw = process.env.REACT_APP_API_URL;
  if (typeof raw !== 'string' || !raw.trim().startsWith('http')) return null;
  return raw.trim().replace(/\/+$/, '');
}

async function searchWebMenu(restaurantName: string): Promise<InternetMenuResult | null> {
  const backendUrl = getProxyUrl();
  if (!backendUrl) {
    console.warn('[Internet Menu] REACT_APP_API_URL not set - web menu search disabled');
    return null;
  }

  try {
    const normalizedName = restaurantName
      .replace(/['"]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    console.log(`[Internet Menu] Searching for menu using ChatGPT API: "${normalizedName}"`);

    const prompt = `I need a comprehensive list of at least 20 menu items from the restaurant "${normalizedName}". Please provide a diverse list including:
- 5-7 appetizers/starters
- 8-10 main entrees/dishes
- 3-5 desserts
- 2-3 beverages or sides

Format your response as a simple list of dish names, one per line. Only include actual menu item names, not descriptions or prices. If you don't know the specific menu for "${normalizedName}", provide at least 20 common dishes that would typically be found at this type of restaurant.`;

    try {
      const response = await fetch(`${backendUrl}/api/openai-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [
            {
              role: 'system',
              content: 'You are a helpful assistant that provides restaurant menu information. Always respond with ONLY a list of menu item names, one per line. Do not include descriptions, prices, or any other text. Just the dish names.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 800,
          temperature: 0.7,
        }),
      });

      // Check HTTP response status
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Internet Menu] ChatGPT API HTTP error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        return null;
      }

      const data = await response.json();
      
      // Check for API errors in response
      if (data.error) {
        console.error('[Internet Menu] ChatGPT API error:', data.error);
        return null;
      }

      // Validate response structure
      if (!data.choices || !Array.isArray(data.choices) || data.choices.length === 0) {
        console.error('[Internet Menu] Invalid ChatGPT response structure:', data);
        return null;
      }

      const menuText = data.choices[0]?.message?.content || '';
      console.log('[Internet Menu] ChatGPT response received', {
        responseLength: menuText.length,
        preview: menuText.substring(0, 100)
      });

      if (!menuText || menuText.length < 10) {
        console.log('[Internet Menu] Empty or invalid response from ChatGPT', {
          menuTextLength: menuText.length,
          menuTextPreview: menuText.substring(0, 50)
        });
        return null;
      }

      // Extract menu items from ChatGPT response
      const menuItems: RestaurantMenuItem[] = [];
      const lines = menuText.split('\n').filter((line: string) => line.trim().length > 0);
      
      console.log(`[Internet Menu] Processing ${lines.length} lines from ChatGPT response`);
      
      for (const line of lines) {
        // Clean up the line - remove numbering, bullets, dashes, etc.
        let itemName = line
          .replace(/^\d+[\.\)]\s*/, '') // Remove numbering (1. 2) etc.)
          .replace(/^[•\-\*]\s*/, '') // Remove bullets
          .replace(/^[-–—]\s*/, '') // Remove dashes
          .replace(/^[A-Z][a-z]+\s*:\s*/, '') // Remove category prefixes (e.g., "Appetizers: ")
          .replace(/\s*-\s*.*$/, '') // Remove descriptions after dash
          .replace(/\s*\$.*$/, '') // Remove prices
          .replace(/\s*\(.*\)$/, '') // Remove parenthetical notes
          .trim();
        
        // Skip lines that are too short or too long
        if (itemName.length < 3 || itemName.length > 60) {
          continue;
        }
        
        // Skip lines that are clearly not menu items (introductory text, explanations, etc.)
        const lowerItemName = itemName.toLowerCase();
        if (lowerItemName.includes('menu') || 
            lowerItemName.includes('restaurant') ||
            lowerItemName.includes('typically') ||
            lowerItemName.includes('common') ||
            lowerItemName.includes('popular') ||
            lowerItemName.includes('dishes') ||
            lowerItemName.includes('items') ||
            lowerItemName.startsWith('here') ||
            lowerItemName.startsWith('some') ||
            lowerItemName.startsWith('these') ||
            lowerItemName.startsWith('the ') ||
            lowerItemName === 'appetizers' ||
            lowerItemName === 'entrees' ||
            lowerItemName === 'desserts' ||
            lowerItemName === 'drinks' ||
            lowerItemName === 'sides') {
          continue;
        }
        
        // Clean up any remaining formatting
        itemName = itemName
          .replace(/^[-–—•\*]\s*/, '')
          .replace(/\s+/g, ' ')
          .replace(/[^\w\s&'-]/g, '') // Keep only alphanumeric, spaces, &, ', and -
          .trim();
        
        // Final validation - ensure it looks like a menu item name
        if (itemName && 
            itemName.length >= 3 && 
            itemName.length <= 60 &&
            /^[A-Za-z]/.test(itemName)) { // Must start with a letter
          menuItems.push({
            name: itemName,
            explanation: `Menu item from ${restaurantName} (found via ChatGPT)`,
            acneImpactScore: 50, // Will be analyzed later
            restaurantName: restaurantName,
          });
        }
      }
      
      console.log(`[Internet Menu] Extracted ${menuItems.length} potential menu items`);

      // If we found menu items, return them
      if (menuItems.length > 0) {
        // Remove duplicates (case-insensitive)
        const uniqueItems = Array.from(
          new Map(menuItems.map(item => [item.name.toLowerCase().trim(), item])).values()
        ).slice(0, 30); // Limit to 30 items
        
        console.log(`[Internet Menu] Found ${menuItems.length} menu items, ${uniqueItems.length} unique items from ChatGPT`);
        console.log(`[Internet Menu] Sample items:`, uniqueItems.slice(0, 5).map(item => item.name));
        
        if (uniqueItems.length > 0) {
        return {
          restaurantName,
          items: uniqueItems,
          source: 'web_search',
            confidence: uniqueItems.length >= 10 ? 'high' : uniqueItems.length >= 5 ? 'medium' : 'low',
          };
        }
      }
      
      console.log('[Internet Menu] No menu items extracted from ChatGPT response', {
        menuTextLength: menuText.length,
        linesProcessed: lines.length,
        itemsExtracted: menuItems.length
      });
    } catch (fetchError: any) {
      console.error('[Internet Menu] ChatGPT API fetch error:', {
        error: fetchError,
        message: fetchError?.message,
        stack: fetchError?.stack
      });
    }
    
    // Always try fallback for known restaurants, even if search failed or returned no items
    console.log('[Internet Menu] Checking fallback menu items for known restaurants');
    const fallbackItems = getFallbackMenuItems(restaurantName);
    if (fallbackItems.length > 0) {
      console.log(`[Internet Menu] Using ${fallbackItems.length} fallback menu items for ${restaurantName}`);
      return {
        restaurantName,
        items: fallbackItems,
        source: 'web_search',
        confidence: 'low',
      };
    }
    
    console.log('[Internet Menu] No fallback items available for this restaurant');
  } catch (error) {
    console.error('[Internet Menu] Error in searchWebMenu:', error);
    // Even on error, try fallback
    const fallbackItems = getFallbackMenuItems(restaurantName);
    if (fallbackItems.length > 0) {
      console.log(`[Internet Menu] Using ${fallbackItems.length} fallback menu items after error`);
      return {
        restaurantName,
        items: fallbackItems,
        source: 'web_search',
        confidence: 'low',
      };
    }
  }
  
  console.log('[Internet Menu] Returning null - no menu found');
  return null;
}

/**
 * Get fallback menu items for well-known restaurants
 * Uses the scalable fallback system from restaurantFallbacks.ts
 */
function getFallbackMenuItems(restaurantName: string): RestaurantMenuItem[] {
  return getRestaurantFallbacks(restaurantName);
}

/**
 * Extract menu items from text (snippets, descriptions, etc.)
 */
function extractMenuItemsFromText(text: string, restaurantName: string): RestaurantMenuItem[] {
  const menuItems: RestaurantMenuItem[] = [];
  
  if (!text || text.length < 10) {
    return menuItems;
  }

  // Common menu item patterns - improved for better extraction
  // These patterns try to catch menu items in various formats
  const patterns = [
    // Pattern: Common dish names (comprehensive list)
    /\b(Orange\s+Chicken|General\s+Tso'?s?\s+Chicken|Kung\s+Pao\s+Chicken|Sweet\s+and\s+Sour\s+Chicken|Mongolian\s+Beef|Beef\s+with\s+Broccoli|Honey\s+Shrimp|Shrimp\s+with\s+Lobster\s+Sauce|Pad\s+Thai|Lo\s+Mein|Chow\s+Mein|Fried\s+Rice|Spring\s+Rolls?|Dumplings?|Wonton\s+Soup|Hot\s+and\s+Sour\s+Soup|Egg\s+Drop\s+Soup|Miso\s+Soup|Teriyaki|Sesame\s+Chicken|Lemon\s+Chicken|Cashew\s+Chicken|Chicken\s+Lo\s+Mein|Beef\s+Lo\s+Mein|Shrimp\s+Lo\s+Mein|Grilled\s+Chicken|Roasted\s+Chicken|Fried\s+Chicken|BBQ\s+Chicken|Chicken\s+Wings|Chicken\s+Tenders|Chicken\s+Sandwich|Chicken\s+Burger|Chicken\s+Salad|Caesar\s+Salad|Garden\s+Salad|Cobb\s+Salad|Greek\s+Salad|Pasta\s+Primavera|Spaghetti|Fettuccine|Penne|Ravioli|Lasagna|Pizza|Margherita|Pepperoni|Hawaiian|Meat\s+Lovers|Veggie\s+Pizza|Burger|Cheeseburger|Bacon\s+Burger|Veggie\s+Burger|Fish\s+and\s+Chips|Fish\s+Tacos|Tacos|Burritos|Quesadilla|Nachos|Guacamole|Salsa|Sushi|Sashimi|Ramen|Pho|Curry|Biryani|Tandoori|Kebab|Gyro|Falafel|Hummus|Baba\s+Ghanoush|Shawarma|Kebab)\b/gi,
    // Pattern: "Item Name - Description" or "Item Name ($Price)"
    /([A-Z][A-Za-z\s&'-]{3,50}?)(?:\s*[-–—]\s*|,\s*[A-Z]|\s*\$|\s*\d+\.\d+|\n|$)/g,
    // Pattern: Bullet points or numbered lists
    /(?:^|\n)[•\-\*]\s*([A-Z][A-Za-z\s&'-]{3,50}?)(?:\s*[-–—]|,|$)/gm,
    // Pattern: Common dish name patterns
    /\b(Chicken\s+\w+|Beef\s+\w+|Salmon\s+\w+|Shrimp\s+\w+|Pasta\s+\w+|Salad\s+\w+|Soup\s+\w+|Steak\s+\w+|Burger\s+\w+|Pizza\s+\w+|Taco\s+\w+|Burrito\s+\w+|Sushi\s+\w+|Ramen\s+\w+|Curry\s+\w+|Stir\s+Fry|Fried\s+Rice|Pad\s+Thai|Lo\s+Mein|Chow\s+Mein)\b/gi,
  ];

  const foundItems = new Set<string>();

  for (const pattern of patterns) {
    const matches = Array.from(text.matchAll(pattern));
    for (const match of matches) {
      const itemName = match[1] || match[0];
      if (itemName && itemName.length > 3 && itemName.length < 60) {
        const cleaned = itemName.trim()
          .replace(/^[•\-\*]\s*/, '')
          .replace(/\s+/g, ' ')
          .replace(/[^\w\s&'-]/g, '')
          .trim();
        
        if (cleaned && !foundItems.has(cleaned.toLowerCase())) {
          foundItems.add(cleaned.toLowerCase());
          menuItems.push({
            name: cleaned,
            explanation: `Menu item from ${restaurantName} (found via web search)`,
            acneImpactScore: 50, // Will be analyzed later
            restaurantName: restaurantName,
          });
        }
      }
    }
  }

  // Fallback: Extract capitalized phrases that look like dish names
  if (menuItems.length < 5) {
    const sentences = text.split(/[.!?]\s+/);
    for (const sentence of sentences) {
      // Look for sentences with common food keywords
      if (/\b(chicken|beef|pork|fish|salmon|shrimp|pasta|rice|noodle|soup|salad|appetizer|entree|dessert|dumpling|spring roll|wonton)\b/i.test(sentence)) {
        // Extract potential dish names (capitalized words)
        const words = sentence.match(/\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\b/g);
        if (words) {
          for (const word of words) {
            if (word.length > 5 && word.length < 40 && !foundItems.has(word.toLowerCase())) {
              foundItems.add(word.toLowerCase());
              menuItems.push({
                name: word,
                explanation: `Menu item from ${restaurantName} (found via web search)`,
                acneImpactScore: 50,
                restaurantName: restaurantName,
              });
            }
          }
        }
      }
    }
  }

  return menuItems;
}

/**
 * Main function to search for restaurant menu on the internet
 */
export async function searchInternetMenu(
  restaurantName: string,
  location?: string
): Promise<InternetMenuResult | null> {
  console.log('[InternetMenuService] Starting search for:', restaurantName);
  
  // Try Google Places API first (most reliable)
  const googleResult = await searchGooglePlaces(restaurantName, location);
  if (googleResult && googleResult.items.length > 0) {
    console.log('[InternetMenuService] Google Places found menu:', googleResult.items.length, 'items');
    return googleResult;
  }
  
  // Try menu API services (Yelp, backend proxy, etc.)
  const apiResult = await searchMenuApis(restaurantName, location);
  if (apiResult && apiResult.items.length > 0) {
    console.log('[InternetMenuService] Menu API found menu:', apiResult.items.length, 'items');
    return {
      restaurantName: apiResult.restaurantName,
      items: apiResult.items,
      source: apiResult.source === 'backend_proxy' ? 'web_search' : 'google_places',
      confidence: apiResult.confidence,
    };
  }
  
  // Fallback to web search (ChatGPT API)
  console.log('[InternetMenuService] Trying ChatGPT API for:', restaurantName);
  const webResult = await searchWebMenu(restaurantName);
  if (webResult && webResult.items.length > 0) {
    console.log('[InternetMenuService] ChatGPT API found menu:', webResult.items.length, 'items');
    return webResult;
  }
  
  // No menu found
  console.log('[InternetMenuService] No menu found for:', restaurantName);
  return null; // Return null instead of empty result to indicate failure
}

/**
 * Check if internet menu search is available (has API keys configured)
 */
export function isInternetMenuSearchAvailable(): boolean {
  const hasGooglePlaces = !!process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
  const hasBackend = !!(typeof process.env.REACT_APP_API_URL === 'string' && process.env.REACT_APP_API_URL.trim().startsWith('http'));

  if (!hasGooglePlaces && !hasBackend) {
    console.warn('[Internet Menu] No API configured. Add REACT_APP_API_URL (Lambda) or REACT_APP_GOOGLE_PLACES_API_KEY.');
  }
  return hasGooglePlaces || hasBackend;
}
